import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { FileText, Download, ExternalLink } from "lucide-react";

// file: { name, dataUrl, file_url, type } — dataUrl is only present for a
// freshly-picked local file; file_url is the persisted Supabase Storage URL
// returned once hydrated from the backend (e.g. after a fresh login). Prefer
// file_url so previews still render for already-uploaded files.
export const FilePreviewDialog = ({ open, onOpenChange, file, docLabel }) => {
  if (!file) return null;
  const src = file.file_url || file.dataUrl;
  const isImage =
    src?.startsWith("data:image") ||
    /\.(png|jpe?g|webp|gif)$/i.test(file.name || "");
  const isPdf =
    src?.startsWith("data:application/pdf") ||
    /\.pdf$/i.test(file.name || "") ||
    file.type === "application/pdf";

  const download = () => {
    if (!src) return;
    const a = document.createElement("a");
    a.href = src;
    a.download = file.name || "document";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="file-preview-dialog">
        <DialogHeader>
          <DialogTitle className="truncate pr-6">{docLabel || file.name}</DialogTitle>
          <DialogDescription className="truncate">{file.name}</DialogDescription>
        </DialogHeader>

        <div className="mt-2 rounded-xl border border-[#E5E7EB] bg-[#F7F7F7] overflow-hidden">
          {src && isImage && (
            <img
              src={src}
              alt={file.name}
              className="w-full max-h-[65vh] object-contain bg-white"
            />
          )}
          {src && isPdf && (
            <iframe
              title={file.name}
              src={src}
              className="w-full h-[65vh] bg-white"
            />
          )}
          {!src && (
            <div className="p-16 flex flex-col items-center text-center">
              <div className="h-14 w-14 rounded-2xl bg-white border border-[#E5E7EB] flex items-center justify-center">
                <FileText className="h-7 w-7 text-[#B60F2A]" />
              </div>
              <div className="mt-4 text-sm font-medium text-[#111]">
                Preview unavailable
              </div>
              <div className="text-xs text-[#6B7280] mt-1 max-w-sm">
                The file bytes are not stored locally (browser storage limit).
                It was uploaded as{" "}
                <span className="font-medium text-[#111]">{file.name}</span>.
              </div>
            </div>
          )}
          {src && !isImage && !isPdf && (
            <div className="p-16 flex flex-col items-center text-center">
              <div className="h-14 w-14 rounded-2xl bg-white border border-[#E5E7EB] flex items-center justify-center">
                <FileText className="h-7 w-7 text-[#B60F2A]" />
              </div>
              <div className="mt-4 text-sm font-medium text-[#111]">
                No inline preview for this file type
              </div>
              <div className="text-xs text-[#6B7280] mt-1">
                Use the download button below to open the file.
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 mt-3">
          {src && (
            <a
              href={src}
              target="_blank"
              rel="noreferrer"
              className="mu-btn-secondary text-sm"
              data-testid="file-preview-open"
            >
              <ExternalLink className="h-4 w-4" /> Open in new tab
            </a>
          )}
          {src && (
            <button
              onClick={download}
              className="mu-btn-primary text-sm"
              data-testid="file-preview-download"
            >
              <Download className="h-4 w-4" /> Download
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
